import {
    usePersonComplianceTicketsMutation,
    usePersonComplianceTicketsQuery
} from "./graphql";
import {Dispatch, useCallback, useEffect, useReducer} from "react";
import {difference} from "lodash";
import {ComplianceTicketFormData, ComplianceTicketFormValues} from "./types";
import {message} from "antd";
import {getCollapsePanelKey} from "./util";
import {ComplianceTicket} from "../../../__generated__/graphql/types";
import * as State from './state';
import {useFormRefManager} from "./form-ref-manager";
import VeriflytTicketEditorInternal from "./ui/editor";
import useVeriflytOrganization from "../../../hooks/veriflyt/veriflyt-organization";

export interface UseVeriflytEditorOptions {
    onSaveSuccess?: () => void,
    onFormStateChanged?: (formState: State.State['formState']) => void,
    // New: when true, the editor operates in read-only mode and disallows changes
    readOnly?: boolean,
}

export function useVeriflytTicketEditorState(personID?: string, options?: UseVeriflytEditorOptions){

    const orgVeriflytAccount = useVeriflytOrganization();

    const queryArgs = {
        personID: personID,
        limit: 9999999
    }

    const { data, loading, error: _error, refetch } =
        usePersonComplianceTicketsQuery(queryArgs);

    const [ saveData, saveDataResult ] = usePersonComplianceTicketsMutation();

    const [ state, _dispatch ] = useReducer(State.Reducer, State.DefaultState);

    useEffect(() => {
        // Notify onFormStateChanged that the formData has changed (will fire even if something else in the state changes)
        if (typeof options?.onFormStateChanged === 'function'){
            options.onFormStateChanged(state.formState);
        }
    }, [ state ]);

    const readOnly = Boolean(options?.readOnly);

    const formRefManager = useFormRefManager();

    function getDeletedTickets(idsInForm: string[]){
        const queriedIds = data.map((datum) => datum._id);
        return difference(queriedIds, idsInForm);
    }

    function handleSubmit(formValues: Map<string, ComplianceTicketFormValues>){

        const _customerVeriflytID = orgVeriflytAccount.organization?.classType === 'flytsuite.customer' ?
            orgVeriflytAccount.veriflytAccount?.veriflytID : null;
        const _employerVeriflytID = orgVeriflytAccount?.organization?.classType === 'flytsuite.employer' ?
            orgVeriflytAccount.veriflytAccount?.veriflytID : null;

        if (!_customerVeriflytID && !orgVeriflytAccount.organization?.veriflytID) {
            message.error('Veriflyt account is not configured for this organization. Please contact support.');
            return;
        }

        const displayedTickets = Array.from(state.formState.values());
        const deletedTicketIds = getDeletedTickets(displayedTickets.map(t => t._id));

        const formValuesList = Array.from(formValues.entries());

        saveData({
            personID: personID,
            deleteTicketIds: deletedTicketIds,
            updateTickets: formValuesList.map(([, values ]) => {

                const completionDate = values.completionDate?.format('YYYY-MM-DD');
                const expirationDate = values.expirationDate?.format('YYYY-MM-DD');

                return {
                    _id: values._id,
                    ticketTemplateID: values.ticketTemplateIDAndName?.key,
                    completionDate: completionDate,
                    expirationDate: expirationDate,
                    onScreenName: values.ticketTemplateIDAndName?.label,
                    result_passed: Boolean(values.result_passed),

                }
            })
        })
            .then(() => {
                message.success('Certification records saved successfully');
                if (typeof options?.onSaveSuccess === 'function'){
                    options.onSaveSuccess();
                }
            })
            .catch(() => {
                message.error('Failed to save certification records')
            })
    }

    function onSave(){
        if (readOnly){
            message.warning('Read-only mode: saving is disabled');
            return;
        }
        const formRefs = formRefManager.getFormRefs();

        const validForms = new Map<string, ComplianceTicketFormValues>();
        let hasErrors = false;

        formRefs.forEach((form, internalId) => {
            form.validateFields((err, values) => {
                console.log('form', values);
                if (err){
                    hasErrors = true;
                }
                else {
                    validForms.set(internalId, values);
                }
            })
        })

        if (!hasErrors){
            handleSubmit(validForms);
        }
    }

    function initTickets(){
        if (data){
            _dispatch({ type: 'INIT_TICKETS', payload: data });
        }
        formRefManager.clear();
    }

    useEffect(() => {
        initTickets();
    }, [ data ]);

    // Adds some side effects to the dispatcher
    const dispatch = useCallback<Dispatch<State.Action>>(
        async (action) => {

            console.debug('action', action);

            if (action.type === 'REMOVE_TICKET'){
                formRefManager.remove(action.payload.internalId);
            }

            if (action.type === 'INIT_TICKETS'){
                formRefManager.clear();
            }

        _dispatch(action);
        },
        [ _dispatch, formRefManager ]
    );

    let error = _error;

    const isPersonConfigured = !loading && _error?.graphQLErrors?.[0]?.errorType !== 'person-not-configured';

    const orgErrors = ['customer-not-configured', 'employer-not-configured'];

    let organizationIsConfigured = true;
    if (!loading && orgErrors.includes(_error?.graphQLErrors?.[0]?.errorType)){
        organizationIsConfigured = false;
    }

    if (!isPersonConfigured || !organizationIsConfigured){
        error = null;
    }

    const api = {
        queryArgs: queryArgs,
        refetch,
        personID: personID,
        loading: loading,
        error: error,
        personIsConfigured: isPersonConfigured,
        organizationIsConfigured: organizationIsConfigured,
        state: state,
        saving: saveDataResult.loading,
        readOnly,
        addFormRef: formRefManager.add,
        save: onSave,
        addBlankTicket: (options?: { makeVisible?: boolean }) => {
            if (readOnly){
                message.warning('Read-only mode: adding records is disabled');
                return;
            }
            dispatch({ type: 'ADD_BLANK_TICKET', payload: { makeVisible: options?.makeVisible } });
        },

        updateTicket: (ticketData: ComplianceTicket | ComplianceTicketFormData) => {
            if (readOnly){
                return; // ignore updates in read-only mode
            }
            dispatch({ type: 'UPDATE_TICKET', payload: { data: ticketData } });
        },

        removeTicket: (ticket: ComplianceTicketFormData | string) => {
            if (readOnly){
                message.warning('Read-only mode: removing records is disabled');
                return;
            }
            let formId = typeof ticket === 'string' ? ticket : ticket?.internalId;
            dispatch({ type: 'REMOVE_TICKET', payload: { internalId: formId } })
        },

        resetTickets: () => {
            initTickets();
        },

        setCollapseKeys: (keys: string[]) => {
            dispatch({ type: 'COLLAPSE_SET_ACTIVE_KEYS', payload: keys })
        },

        setTicketVisibility: (ticket: ComplianceTicketFormData, visible: boolean) => {
            const key = getCollapsePanelKey(ticket);
            dispatch({ type: 'COLLAPSE_SET_VISIBILITY', payload: { key, visible } })
        }
    }

    return api;
}

export type VeriflytTicketEditorApi = ReturnType<typeof useVeriflytTicketEditorState>;

type VeriflytTicketEditorComponent = typeof VeriflytTicketEditorInternal & { useState: typeof useVeriflytTicketEditorState }

const VeriflytTicketEditor: VeriflytTicketEditorComponent = VeriflytTicketEditorInternal as VeriflytTicketEditorComponent;
VeriflytTicketEditor.useState = useVeriflytTicketEditorState;

export default VeriflytTicketEditor;
